import { calculateTotals } from './pricing';

/**
 * Builds a Zoho CRM payload aligned with IMC_Schools API field names.
 *
 * IMC_Schools fields used:
 *  Name, ACSI_ID_No, Address_Line_1, Address_Line_2, City, State, Country,
 *  School_Code, Advisor_Name, Advisor_Title, Email, Phone_1, Secondary_Email,
 *  Secondary_Advisor_Name, Secondary_Advisor_Title, Secondary_Advisor_Email,
 *  Secondary_Advisor_Phone_2, Billing_Contact, School_Phone, Added_New_Students,
 *  Level_1_Payment_Amount,
 *  Students (subform: Student_Name, Grade_Level, Test, Student_ID)
 */
export function buildCrmPayload(form, paymentMeta = {}) {
  const totals = calculateTotals(form.students || []);

  const primary = form.contacts[0] || {};
  const secondary = form.contacts[1] || {};

  const advisorName = [primary.firstName, primary.lastName]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');

  const secondaryAdvisorName = [secondary.firstName, secondary.lastName]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');

  const addressLine2 = [form.school.address2.trim(), form.school.zip.trim()]
    .filter(Boolean)
    .join(form.school.address2.trim() && form.school.zip.trim() ? ', ' : '');

  const studentsSubform = (form.students || [])
    .filter(
      (student) =>
        student.firstName?.trim() ||
        student.lastName?.trim() ||
        student.gradeLevel,
    )
    .map((student, index) => {
      const studentName = [student.firstName, student.lastName]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ');
      const tests = [
        student.tests.reasoning ? 'Reasoning' : null,
        student.tests.computation ? 'Computation' : null,
      ].filter(Boolean);

      return {
        Student_Name: studentName,
        Grade_Level: student.gradeLevel,
        Test: tests,
        Student_ID: student.id || `S${index + 1}`,
      };
    });

  /** Flat IMC_Schools record ready for Zoho CRM insert/update */
  const imcSchool = {
    Name: form.school.name.trim(),
    ACSI_ID_No: form.school.acsiId.trim(),
    Address_Line_1: form.school.address1.trim(),
    Address_Line_2: addressLine2,
    City: form.school.city.trim(),
    State: form.school.state.trim(),
    Country: form.school.country,
    School_Code: form.school.schoolCode.trim().toUpperCase(),
    Advisor_Name: advisorName,
    Advisor_Title: String(primary.title || '').trim(),
    Email: String(primary.email || '').trim(),
    Phone_1: String(primary.phone || '').trim(),
    School_Phone: String(primary.phone || '').trim(),
    Billing_Contact: String(primary.email || '').trim(),
    Secondary_Email: String(secondary.email || '').trim(),
    Secondary_Advisor_Name: secondaryAdvisorName,
    Secondary_Advisor_Title: String(secondary.title || '').trim(),
    Secondary_Advisor_Email: String(secondary.email || '').trim(),
    Secondary_Advisor_Phone_2: String(secondary.phone || '').trim(),
    Added_New_Students: studentsSubform.length > 0,
    Level_1_Payment_Amount: totals.total,
    Students: studentsSubform,
  };

  return {
    modules: {
      IMC_Schools: imcSchool,
      /** Kept for payment / PDF metadata; not a separate CRM module write by default */
      Registration: {
        School_Code: imcSchool.School_Code,
        School_Name: imcSchool.Name,
        Registering_Students: studentsSubform.length > 0,
        Competition_Level: form.competitionLevel,
        Registration_Method: form.registrationMethod,
        Total_Test_Count: form.totalTestCount,
        Student_Count: studentsSubform.length,
        Student_Fees: totals.studentFees,
        Total_Amount: totals.total,
        Currency: 'USD',
        Accepted_Terms: form.acceptTerms,
        Accepted_Cancellation_Policy: form.acceptCancellation,
        Submitted_At: new Date().toISOString(),
        Payment: {
          Provider: paymentMeta.method === 'check' ? 'Check' : 'Stripe',
          Method: paymentMeta.method || 'card',
          Status: paymentMeta.status || 'pending',
          Check_Number: paymentMeta.checkNumber || null,
          Payment_Intent_Id: paymentMeta.paymentIntentId || null,
        },
      },
    },
    formSnapshot: {
      school: form.school,
      contacts: form.contacts,
      registeringStudents: studentsSubform.length > 0 ? 'yes' : 'no',
      registrationMethod: form.registrationMethod,
      totalTestCount: form.totalTestCount,
      competitionLevel: form.competitionLevel,
      students: form.students,
      totals,
    },
  };
}
